/**
 * React Query Hook: Save Branch File
 *
 * Saves file content to a branch.
 * - First save (no active branch): creates a new branch — `name` is required.
 * - Subsequent saves: updates the existing active branch — `name` is ignored.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { SAVE_BRANCH_FILE_MUTATION } from '@/services/metadata_v1/queries';
import type {
  MetadataBranch,
  SaveBranchFileResponse,
  SaveBranchFileVariables,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

async function saveBranchFile(
  variables: SaveBranchFileVariables
): Promise<MetadataBranch> {
  const response = await graphqlRequest<SaveBranchFileResponse>(
    SAVE_BRANCH_FILE_MUTATION,
    variables
  );

  if (!response?.saveBranchFile) {
    throw new Error('Save failed. Please try again.');
  }

  return response.saveBranchFile;
}

export interface UseSaveBranchFileInput {
  metadataItemId: string;
  content: string;
  /** Required on first save (when no active branch exists). Ignored on updates. */
  name?: string;
}

export interface UseSaveBranchFileOptions {
  onSuccess?: (data: MetadataBranch) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to save file content to a branch.
 *
 * Automatically creates a branch on first save (requires `name`) or updates
 * the existing active branch on subsequent saves.
 *
 * Invalidates `activeBranch` and `userBranches` caches on success.
 */
export function useSaveBranchFile(
  options?: UseSaveBranchFileOptions
): UseMutationResult<MetadataBranch, Error, UseSaveBranchFileInput> {
  const queryClient = useQueryClient();
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  return useMutation({
    mutationFn: ({ metadataItemId, content, name }: UseSaveBranchFileInput) =>
      saveBranchFile({
        input: { metadataItemId, content, name },
      }),
    onSuccess: (data) => {
      // Invalidate every activeBranch query — tab id may have been a chat/virtual
      // key while the branch was saved under metadataItem.id, so a narrow key
      // would miss the subscription the UI actually uses.
      queryClient.invalidateQueries({ queryKey: ['activeBranch'] });

      queryClient.invalidateQueries({
        queryKey: ['userBranches', organizationId],
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
